<template>
    <!-- This example requires Tailwind CSS v2.0+ -->
    <div
        class="group mb-4 w-full rounded-lg border-iron-200 px-2 py-2.5 shadow-sm ring-1 ring-iron-200 transition-all duration-200 sm:px-4 sm:py-5"
        :class="{
            'cursor-pointer bg-white hover:shadow-lg hover:ring-2 hover:ring-z-primary-400':
                eventType.enabled_at,
            'bg-zinc-100': !eventType.enabled_at,
        }"
        @click="goToEventType"
    >
        <div class="flex justify-between">
            <div class="grid grid-cols-[20px_1fr]">
                <div class="pt-1.5">
                    <div class="flex-shrink-0"></div>
                    <div
                        class="h-3 w-3 rounded-full"
                        :class="{
                            'bg-green-500': eventType.enabled_at,
                            'bg-red-500': !eventType.enabled_at,
                        }"
                    ></div>
                </div>

                <div>
                    <p
                        class="min-w-0 flex-1 p-1 font-medium"
                        :class="{
                            'text-iron-900': eventType.enabled_at,
                            'text-iron-400': !eventType.enabled_at,
                        }"
                    >
                        {{ eventType.name }}
                    </p>

                    <!-- <PreviewPublicUrlCopy
                        class="hidden sm:flex"
                        :event-type="eventType"
                    /> -->

                    <div class="flex gap-2">
                        <div
                            v-if="eventType.duration !== null"
                            class="mt-2 flex items-center gap-1"
                        >
                            <Clock class="text-iron-400" :size="20" />
                            <span class="text-xs font-medium text-iron-500"
                                >{{ eventType.duration }} min</span
                            >
                        </div>

                        <div
                            v-if="eventType.stripe_config"
                            class="mt-2 flex items-center gap-1"
                        >
                            <CircleDollarSign
                                class="text-iron-400"
                                :size="20"
                            />
                            <span class="text-xs font-medium text-iron-500"
                                >{{ eventType.stripe_config.price / 100 }}
                                {{ eventType.stripe_config.currency }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            <div class="flex justify-end sm:mt-0 md:justify-start">
                <div class="inline-flex">
                    <a
                        v-if="eventType.enabled_at"
                        target="_blank"
                        :href="
                            route('public.event-types.show', {
                                eventTypeUuid: eventType.uuid,
                            })
                        "
                        class="relative inline-flex max-h-[36px] cursor-pointer items-center gap-2 rounded-l-md bg-white p-2 text-sm font-medium text-iron-900 ring-1 ring-inset ring-iron-300 hover:bg-iron-50 focus:z-10"
                        @click.stop
                    >
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger>
                                    <Eye class="text-iron-500" :size="20" />
                                </TooltipTrigger>
                                <TooltipContent :side-offset="10">
                                    <p>
                                        {{
                                            $t(
                                                "event_types.event_preview.open_booking_page",
                                            )
                                        }}
                                    </p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </a>

                    <button
                        v-if="isSupported && eventType.enabled_at"
                        type="button"
                        class="relative -ml-px inline-flex max-h-[36px] items-center bg-white p-2 text-sm font-medium ring-1 ring-inset ring-iron-300 hover:bg-iron-50 focus:z-10"
                        @click.stop="copyEventTypePreviewToClipboard"
                    >
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger>
                                    <Transition mode="out-in">
                                        <Link
                                            v-if="!copied"
                                            class="text-iron-500"
                                            :size="20"
                                        />
                                        <ClipboardCheck
                                            v-else
                                            class="text-iron-500"
                                            :size="20"
                                        />
                                    </Transition>
                                </TooltipTrigger>
                                <TooltipContent :side-offset="10">
                                    <p>
                                        {{
                                            $t(
                                                "event_types.event_preview.copy_url",
                                            )
                                        }}
                                    </p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </button>

                    <button
                        v-if="!eventType.enabled_at"
                        type="button"
                        class="relative mr-3 inline-flex max-h-[36px] items-center rounded-l-md rounded-r-md bg-white py-3.5 text-sm font-medium ring-1 ring-inset ring-iron-300 hover:bg-iron-50 focus:z-10"
                    >
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger>
                                    <Transition mode="out-in">
                                        <button
                                            class="flex text-iron-600"
                                            @click.stop="enableEventType"
                                        >
                                            <span class="px-2">
                                                <Zap size="20" />
                                            </span>
                                            <span class="pr-3">{{
                                                $t(
                                                    "event_types.event_preview.enable",
                                                )
                                            }}</span>
                                        </button>
                                    </Transition>
                                </TooltipTrigger>
                                <TooltipContent :side-offset="10">
                                    <p>
                                        {{
                                            $t(
                                                "event_types.event_preview.enable",
                                            )
                                        }}
                                    </p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </button>

                    <IntegrationCodeModal
                        v-if="eventType.enabled_at"
                        :token="props.eventType.tokens[0].token"
                    >
                        <button
                            class="relative -ml-px inline-flex max-h-[36px] cursor-pointer items-center gap-2 bg-white p-2 text-sm font-medium ring-1 ring-inset ring-iron-300 hover:bg-iron-50 focus:z-10"
                            :class="{
                                'text-iron-900': eventType.enabled_at,
                                'text-iron-400': !eventType.enabled_at,
                            }"
                            @click.stop
                        >
                            <Code class="text-iron-500" :size="20" />
                        </button>
                    </IntegrationCodeModal>

                    <Menu as="div" class="relative -ml-px block">
                        <MenuButton
                            class="relative inline-flex items-center rounded-r-md bg-white p-2 text-iron-400 ring-1 ring-inset ring-iron-300 hover:bg-iron-50 focus:z-10"
                            :class="{
                                'rounded-l-md': !eventType.enabled_at,
                            }"
                            @click.stop
                        >
                            <span class="sr-only">Open options</span>
                            <MoreVertical class="text-iron-500" :size="20" />
                        </MenuButton>
                        <transition
                            enter-active-class="transition ease-out duration-100"
                            enter-from-class="transform opacity-0 scale-95"
                            enter-to-class="transform opacity-100 scale-100"
                            leave-active-class="transition ease-in duration-75"
                            leave-from-class="transform opacity-100 scale-100"
                            leave-to-class="transform opacity-0 scale-95"
                        >
                            <MenuItems
                                class="absolute right-0 z-10 -mr-1 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                            >
                                <MenuItem
                                    v-for="item in items"
                                    :key="item.name"
                                    v-slot="{ active }"
                                >
                                    <div>
                                        <component
                                            :is="
                                                item.href
                                                    ? 'InertiaLink'
                                                    : 'button'
                                            "
                                            :href="item.href || undefined"
                                            class="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm font-medium"
                                            :class="[
                                                active && item.type === 'danger'
                                                    ? 'bg-error-25'
                                                    : active
                                                      ? 'bg-iron-50 text-iron-900'
                                                      : 'text-iron-700',
                                                item.type === 'danger'
                                                    ? 'text-error-500'
                                                    : 'text-iron-500',
                                            ]"
                                            @click.stop="
                                                item.onClick
                                                    ? item.onClick()
                                                    : () => {}
                                            "
                                        >
                                            <component
                                                :is="item.icon"
                                                :class="[
                                                    item.type === 'danger'
                                                        ? 'text-error-500'
                                                        : 'text-iron-500',
                                                ]"
                                                :size="20"
                                            ></component>
                                            {{ item.name }}
                                        </component>
                                    </div>
                                </MenuItem>
                            </MenuItems>
                        </transition>
                    </Menu>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { onClickOutside } from "@vueuse/core";
import { router } from "@inertiajs/vue3";
import useConfirm from "../ConfirmDialog/composables/useConfirm";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue";
import route from "ziggy-js";
import { useClipboard } from "@vueuse/core";
import { useI18n } from "vue-i18n";
import IntegrationCodeModal from "./components/IntegrationCodeModal.vue";
import type { EventType } from "./types/EventType";

import {
    Eye,
    Link,
    MoreVertical,
    Pencil,
    Code,
    Trash,
    ClipboardCheck,
    Clock,
    CopyPlus,
    ZapOff,
    Zap,
    CircleDollarSign,
} from "lucide-vue-next";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { useHasSubscription } from "@/composables/useHasSubscription";
import useProDialog from "@/ProModal/composables/useProDialog";

const props = defineProps<{
    eventType: EventType;
}>();

type MenuItemType = {
    name: string;
    href?: string;
    onClick?: () => void;
    icon: any;
    type?: "danger";
};

const target = ref(null);
const opened = ref(false);

const { show } = useConfirm();

const { copy, copied, isSupported } = useClipboard();
const { hasSubscription } = useHasSubscription();

const proDialog = useProDialog();

const { t } = useI18n();

onClickOutside(target, () => (opened.value = false));

const removeEventType = async () => {
    const result = await show({
        header: `
            ${t("event_types.event_preview.remove_detail.title")}: ${
                props.eventType.name
            }?`,
        message: "",
        confirm: t("event_types.event_preview.remove_detail.remove_event_type"),
        cancel: t("cancel"),
    });

    if (result) {
        await router.delete(`event-types/${props.eventType.id}`);
    }
};

function copyEventTypePreviewToClipboard() {
    copy(
        route("public.event-types.show", {
            eventTypeUuid: props.eventType.uuid,
        }),
    );
}

function copyTokenToClipboard() {
    copy(props.eventType.tokens[0].token);
}

async function duplicateEventType() {
    if (!hasSubscription) {
        proDialog.show({
            feature: "unlimited_events",
        });
    } else {
        await router.post(
            route("app.event-types.duplicate", {
                eventType: props.eventType.id,
            }),
        );
    }
}

function goToEventType() {
    if (!props.eventType.enabled_at) {
        return;
    }

    router.visit(
        route("app.event-types.show", {
            eventType: props.eventType.id,
        }),
    );
}

async function enableEventType() {
    const result = await show({
        header: `
            ${t("event_types.event_preview.enable_detail.title")}: ${
                props.eventType.name
            }?`,
        message: t("event_types.event_preview.enable_detail.description"),
        confirm: t("event_types.event_preview.enable"),
        cancel: t("cancel"),
    });

    if (result) {
        await router.post(
            route("app.event-types.enable", { eventType: props.eventType.id }),
        );
    }
}

async function disableEventType() {
    const result = await show({
        header: `
            ${t("event_types.event_preview.disable_detail.title")}: ${
                props.eventType.name
            }?`,
        message: t("event_types.event_preview.disable_detail.description"),
        confirm: t(
            "event_types.event_preview.disable_detail.disable_event_type",
        ),
        cancel: t("cancel"),
    });

    if (result) {
        await router.post(
            route("app.event-types.disable", { eventType: props.eventType.id }),
        );
    }
}

const items = computed((): MenuItemType[] => {
    return [
        {
            name: t("event_types.event_preview.configure"),
            href: route("app.event-types.show", {
                eventType: props.eventType.id,
            }),
            icon: Pencil,
        },
        {
            name: t("event_types.event_preview.duplicate"),
            onClick: duplicateEventType,
            icon: CopyPlus,
        },
        {
            name: t("event_types.event_preview.copy_token"),
            onClick: copyTokenToClipboard,
            icon: Code,
        },
        {
            name: props.eventType.enabled_at
                ? t("event_types.event_preview.disable")
                : t("event_types.event_preview.enable"),
            onClick: props.eventType.enabled_at
                ? disableEventType
                : enableEventType,
            icon: props.eventType.enabled_at ? ZapOff : Zap,
        },
        {
            name: t("event_types.event_preview.remove"),
            onClick: removeEventType,
            icon: Trash,
            type: "danger",
        },
    ];
});
</script>

<style>
@import url("/node_modules/@zaptime/vue3/dist/style.css");
</style>
